/**
 * Generic editor screen (P1-T14) — `#/editor/<tool>?template=<slug>&quality=<tier>`.
 *
 * Config-driven renderer: NO per-tool code. The screen reads the `template`
 * query param → GET /templates/:slug → renders from its `editorSchema`:
 *
 *   editorSchema = {
 *     inputs:  ['photo', 'face'],            // upload slots (1 or 2)
 *     options: ['quality', 'prompt',         // quality tiers / free prompt
 *               'gender',                    // Female/Male chips
 *               'hairstyle:bob',             // preset groups (name:value)
 *               'color:black'],
 *   }
 *
 * Without a `template` param (direct tool entry from quick actions) a minimal
 * built-in schema per MVP tool is used (change_outfit, face_swap_image,
 * hair_salon, image_to_video).
 *
 * Flow: upload box(es) → tap → file picker → POST /uploads/request →
 * PUT bytes (progress) → POST /uploads/:fileId/complete → thumbnail preview.
 * Generate (sticky `.btn-generate` money button) shows the EXACT ✦ cost
 * (template base cost scaled by the selected quality tier via /credits/pricing),
 * stays disabled until every required input is valid, checks the balance on
 * tap (→ #/paywall when short) and POSTs /ai/jobs → #/result/:jobId.
 *
 * States: loading skeleton, template 404, unknown tool, API error with retry.
 */
import { ApiError, api } from '../api/client.js';
import { navigate } from '../router.js';
import { escapeHtml } from '../components/placeholder.js';
import { icon } from '../components/icon.js';
import { bindCompareSliders, compareSliderHtml } from '../components/compareSlider.js';
import { KNOWN_INPUTS, inputLabels, twoPhotoExampleHtml } from '../components/twoPhotoExample.js';
import { withUploadGuide } from '../components/uploadGuide.js';
import { toast, toastApiError } from '../ui/toast.js';
import { creditsChanged, getBalance, refreshCredits } from '../state/credits.js';
import { requireAccount, requireSignIn } from '../state/gate.js';
import { renderImageToVideo } from './imageToVideo.js';
import { renderChangeOutfit } from './changeOutfit.js';
import { renderHairSalon } from './hairSalon.js';
import { renderMakeupStyles } from './makeupStyles.js';
import { renderFaceExpression } from './faceExpression.js';
import { renderPhotoEnhance } from './photoEnhance.js';
import { renderRemoveObject } from './removeObject.js';
import { renderRetouch } from './retouch.js';
import { renderReshapeBody } from './reshapeBody.js';

import { renderCombine } from './combine.js';
import { renderTransition } from './transition.js';
import { renderIngredients } from './ingredients.js';
import type {
  CreditPricingConfig,
  QualityTier,
  Template,
  ToolType,
  UploadFileMeta,
  VideoDurationSec,
} from '@loomia/shared';

/** Reshape Body screen; off while the model's results are too weak. */
const RESHAPE_BODY_ENABLED = false;

// ---------------------------------------------------------------------------
// Schema model
// ---------------------------------------------------------------------------

interface UploadSlotDef {
  key: string;
  label: string;
  hint: string;
  required: boolean;
}

interface OptionDef {
  value: string;
  label: string;
  /** Optional color dot (e.g. hair colors). */
  swatch?: string;
  imageUrl?: string;
}

type OptionGroupKind = 'chips' | 'presets' | 'prompt' | 'toggle';

interface OptionGroupDef {
  key: string;
  label: string;
  kind: OptionGroupKind;
  options: OptionDef[];
  required: boolean;
  /** Currently selected value (or free text for 'prompt'). */
  selected: string | null;
  placeholder?: string;
}

interface EditorSpec {
  title: string;
  description: string;
  uploads: UploadSlotDef[];
  groups: OptionGroupDef[];
  /** Quality tiers offered (template.qualityTiers, or tool defaults). */
  tiers: QualityTier[];
  /** Base ✦ cost before tier scaling. */
  baseCost: number;
  templateId?: string;
}

type SlotStatus = 'empty' | 'uploading' | 'ready' | 'error';

interface SlotState {
  def: UploadSlotDef;
  status: SlotStatus;
  /** 0..1 while uploading. */
  progress: number;
  fileId: string | null;
  previewUrl: string | null;
  /** Kept in memory so "retry" doesn't need the picker again. */
  file: File | null;
  error: string | null;
}

const VALID_TOOLS: readonly ToolType[] = [
  'image_to_video',
  'edit_image',
  'change_outfit',
  'face_swap_image',
  'face_swap_video',
  'hair_salon',
  'makeup_styles',
  'face_expression',
  'photo_enhance',
  'remove_object',
  'retouch',
  'ingredients',
  'reshape_body',
  'combine',
  'transition',
];

/** Human labels for the tool routes. */
const TOOL_LABELS: Record<ToolType, string> = {
  image_to_video: 'Image To Video',
  edit_image: 'Edit Image',
  change_outfit: 'Change Outfit',
  face_swap_image: 'Face Swap',
  face_swap_video: 'Face Swap Video',
  hair_salon: 'Hair Salon',
  makeup_styles: 'Makeup Styles',
  face_expression: 'Face Expression',
  photo_enhance: 'Photo Enhance',
  remove_object: 'Remove Object',
  retouch: 'Retouch',
  ingredients: 'Ingredients',
  reshape_body: 'Reshape Body',
  combine: 'Combine',
  transition: 'Transition',
};

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
/** Client-side sanity cap; the server enforces the real limit. */
const CLIENT_MAX_BYTES = 25 * 1024 * 1024;

function titleCase(s: string): string {
  return s
    .split(/[-_]+/)
    .map((w) => (w ? w[0]!.toUpperCase() + w.slice(1) : w))
    .join(' ');
}

function newId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `id-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
}

// ---------------------------------------------------------------------------
// Schema builders
// ---------------------------------------------------------------------------

/** Interpret a template's loose editorSchema into the renderer's model. */
function normalizeTemplateSchema(t: Template): EditorSpec {
  const raw = (t.editorSchema ?? {}) as Record<string, unknown>;
  const strArray = (v: unknown): string[] =>
    Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  const inputs = strArray(raw['inputs']);
  const options = strArray(raw['options']);

  const uploads: UploadSlotDef[] = (inputs.length > 0 ? inputs : ['photo']).map((name) => {
    const known = KNOWN_INPUTS[name];
    return {
      key: name,
      label: known?.label ?? titleCase(name),
      hint: known?.hint ?? 'Tap to choose a photo',
      required: true,
    };
  });

  const groups: OptionGroupDef[] = [];
  for (const opt of options) {
    if (opt === 'quality') continue; // rendered from template.qualityTiers
    if (opt === 'prompt') {
      groups.push({
        key: 'prompt',
        label: 'Describe it',
        kind: 'prompt',
        options: [],
        required: false,
        selected: null,
        placeholder: 'Add details for the AI (optional)…',
      });
      continue;
    }
    if (opt === 'gender') {
      groups.push({
        key: 'gender',
        label: 'Gender',
        kind: 'chips',
        required: true,
        selected: 'female',
        options: [
          { value: 'female', label: 'Female' },
          { value: 'male', label: 'Male' },
        ],
      });
      continue;
    }
    const sep = opt.indexOf(':');
    if (sep > 0) {
      // Preset group pinned by the template, e.g. 'outfit:winter-coat'.
      const name = opt.slice(0, sep);
      const value = opt.slice(sep + 1);
      groups.push({
        key: name,
        label: titleCase(name),
        kind: 'presets',
        required: true,
        selected: value,
        options: [{ value, label: titleCase(value) }],
      });
      continue;
    }
    // Unknown bare option: ignore (server owns the contract).
    console.warn('[editor] ignoring unknown schema option:', opt);
  }

  return {
    title: t.title,
    description: t.description,
    uploads,
    groups,
    tiers: t.qualityTiers ?? [],
    baseCost: t.creditCost,
    templateId: t.id,
  };
}

/** Minimal built-in schemas for direct tool entry (no template). */
function defaultSpec(tool: ToolType, pricing: CreditPricingConfig): EditorSpec | null {
  const base = pricing.perTool[tool]?.credits ?? 30;
  const photo: UploadSlotDef = {
    key: 'photo',
    label: 'Your photo',
    hint: 'Clear, front-facing, good lighting',
    required: true,
  };
  switch (tool) {
    case 'change_outfit':
      return {
        title: 'Change Outfit',
        description: 'Upload your photo, pick an outfit, and see yourself wearing it.',
        uploads: [{ ...photo, hint: 'Half-body photo works best' }],
        groups: [
          {
            key: 'outfit',
            label: 'Outfit',
            kind: 'presets',
            required: true,
            selected: null,
            options: [
              { value: 'winter-coat', label: 'Winter Coat' },
              { value: 'lace-dress', label: 'Lace Dress' },
              { value: 'streetwear', label: 'Streetwear' },
              { value: 'suit', label: 'Classic Suit' },
            ],
          },
        ],
        tiers: [],
        baseCost: base,
      };
    case 'face_swap_image':
      return {
        title: 'Face Swap',
        description: 'Swap a face into your photo. Both photos should be clear and front-facing.',
        uploads: [
          { ...photo, label: 'Original photo', hint: 'The photo to edit' },
          { key: 'face', label: 'Face photo', hint: 'The face to swap in', required: true },
        ],
        groups: [],
        tiers: [],
        baseCost: base,
      };
    case 'hair_salon':
      return {
        title: 'Hair Salon',
        description: 'Try new hairstyles and colors on your photo.',
        uploads: [{ ...photo, hint: 'Face clearly visible; hair tied back is ideal' }],
        groups: [
          {
            key: 'gender',
            label: 'Gender',
            kind: 'chips',
            required: true,
            selected: 'female',
            options: [
              { value: 'female', label: 'Female' },
              { value: 'male', label: 'Male' },
            ],
          },
          {
            key: 'hairstyle',
            label: 'Hairstyle',
            kind: 'presets',
            required: true,
            selected: null,
            options: [
              { value: 'bob', label: 'K-pop Bob' },
              { value: 'long-layers', label: 'Long Layers' },
              { value: 'pixie', label: 'Pixie' },
              { value: 'curtain-bangs', label: 'Curtain Bangs' },
              { value: 'wavy-lob', label: 'Wavy Lob' },
            ],
          },
          {
            key: 'color',
            label: 'Hair color',
            kind: 'chips',
            required: true,
            selected: 'black',
            options: [
              { value: 'black', label: 'Black', swatch: '#1c1c1e' },
              { value: 'dark-brown', label: 'Dark brown', swatch: '#4a3226' },
              { value: 'light-brown', label: 'Light brown', swatch: '#8a5a33' },
              { value: 'blonde', label: 'Blonde', swatch: '#d9b36c' },
              { value: 'red', label: 'Red', swatch: '#a83232' },
            ],
          },
        ],
        tiers: [],
        baseCost: base,
      };
    case 'image_to_video':
      return {
        title: 'Image To Video',
        description: 'Bring a still photo to life with AI motion.',
        uploads: [{ ...photo, hint: 'High-resolution photos give smoother motion' }],
        groups: [
          {
            key: 'prompt',
            label: 'Motion prompt',
            kind: 'prompt',
            required: false,
            selected: null,
            options: [],
            placeholder: 'e.g. slow cinematic push-in, subtle smile…',
          },
        ],
        tiers: ['360p', '540p', '720p', '1080p'],
        baseCost: base,
      };
    default:
      return null;
  }
}

/** Exact ✦ cost: per-tier override wins, else the template/base cost. */
function computeCost(
  spec: EditorSpec,
  pricing: CreditPricingConfig,
  tool: ToolType,
  tier: QualityTier | null,
  durationSec: VideoDurationSec | null = null,
): number {
  const matrix = pricing.perTool[tool]?.perTierDuration;
  if (matrix) {
    const price = matrix[tier ?? '540p']?.[durationSec ?? 5];
    if (typeof price === 'number') return price;
  }
  if (tier) {
    const perTier = pricing.perTool[tool]?.perTier?.[tier];
    if (typeof perTier === 'number') return perTier;
  }
  return spec.baseCost;
}

/** Clip lengths this tool is priced at (empty = not a duration-priced tool). */
function durationsFor(pricing: CreditPricingConfig, tool: ToolType): VideoDurationSec[] {
  const entry = pricing.perTool[tool];
  if (!entry?.perTierDuration) return [];
  return entry.durations && entry.durations.length > 0 ? entry.durations : [5, 10, 15];
}

function durationSectionHtml(
  spec: EditorSpec,
  pricing: CreditPricingConfig,
  tool: ToolType,
  tier: QualityTier | null,
  durations: VideoDurationSec[],
  duration: VideoDurationSec | null,
): string {
  if (durations.length === 0) return '';
  return `
    <section class="ed-group" aria-label="Duration">
      <h2 class="section-title">Duration</h2>
      <div class="chips" role="radiogroup" aria-label="Video duration" id="ed-durations">
        ${durations
          .map(
            (d) => `
          <button
            class="chip${d === duration ? ' selected' : ''}"
            role="radio"
            aria-checked="${d === duration}"
            data-duration="${d}"
          ><span class="ed-chip-main">${d}s</span><span class="ed-chip-sub" data-dprice="${d}">\u2726 ${computeCost(spec, pricing, tool, tier, d)}</span></button>`,
          )
          .join('')}
      </div>
      <p class="muted ed-note">Longer videos cost more credits.</p>
    </section>`;
}

function preferredTier(tiers: QualityTier[], wanted: string | null): QualityTier | null {
  if (tiers.length === 0) return null;
  if (wanted && (tiers as string[]).includes(wanted)) return wanted as QualityTier;
  return tiers[0]!; // lowest tier = cheapest; people upgrade by choice
}

// ---------------------------------------------------------------------------
// Markup
// ---------------------------------------------------------------------------

function skeletonHtml(): string {
  return `
    <div class="ed" aria-busy="true" aria-label="Loading editor">
      <div class="ed-head">
        <span class="shimmer-line" style="width: 32px; height: 32px; border-radius: 10px;"></span>
        <span class="shimmer-line" style="width: 45%; height: 20px;"></span>
      </div>
      <div class="stack ed-body">
        <div class="ed-uploads"><div class="ed-slot"><span class="shimmer"></span></div></div>
        <span class="shimmer-line"></span>
        <span class="shimmer-line short"></span>
        <div class="chips"><span class="chip"></span><span class="chip"></span><span class="chip"></span></div>
      </div>
    </div>`;
}

function errorHtml(message: string, retry: boolean): string {
  return `
    <div class="stack">
      <div class="empty" style="padding-top: 72px;">
        <div class="empty-icon">📡</div>
        <p class="muted" style="max-width: 260px;">${escapeHtml(message)}</p>
        ${
          retry
            ? '<button class="btn btn-secondary" data-action="retry">Try again</button>'
            : '<a class="btn btn-secondary" href="#/home">Back home</a>'
        }
      </div>
    </div>`;
}

function slotHtml(slot: SlotState): string {
  const { def } = slot;
  if (slot.status === 'ready' && slot.previewUrl) {
    return `
      <div class="ed-slot is-ready">
        <img src="${escapeHtml(slot.previewUrl)}" alt="${escapeHtml(def.label)}" />
        <span class="ed-slot-label">${escapeHtml(def.label)}</span>
        <div class="ed-slot-actions">
          <button class="ed-slot-btn" data-slot-action="replace" aria-label="Replace ${escapeHtml(def.label)}">${icon('refresh')}</button>
          <button class="ed-slot-btn" data-slot-action="remove" aria-label="Remove ${escapeHtml(def.label)}">${icon('close')}</button>
        </div>
      </div>`;
  }
  if (slot.status === 'uploading') {
    const pct = Math.round(slot.progress * 100);
    return `
      <div class="ed-slot is-busy" aria-label="Uploading ${escapeHtml(def.label)}">
        ${
          slot.previewUrl
            ? `<img src="${escapeHtml(slot.previewUrl)}" alt="" aria-hidden="true" />`
            : `<span class="ed-slot-plus" aria-hidden="true">${icon('add_photo_alternate')}</span>`
        }
        <div class="ed-progress" role="progressbar" aria-valuenow="${pct}" aria-valuemin="0" aria-valuemax="100">
          <span style="width: ${pct}%;"></span>
        </div>
        <span class="ed-slot-label">${pct}%</span>
      </div>`;
  }
  if (slot.status === 'error') {
    return `
      <button class="ed-slot is-error" data-slot-action="retry" aria-label="Retry upload for ${escapeHtml(def.label)}">
        <span class="ed-slot-plus" aria-hidden="true">${icon('cloud_off')}</span>
        <span class="ed-slot-label">${escapeHtml(def.label)}</span>
        <span class="ed-slot-hint">${escapeHtml(slot.error ?? 'Upload failed. Tap to retry.')}</span>
      </button>`;
  }
  return `
    <button class="ed-slot" data-slot-action="pick" aria-label="Upload ${escapeHtml(def.label)}">
      <span class="ed-slot-plus" aria-hidden="true">${icon('add_photo_alternate')}</span>
      <span class="ed-slot-label">${escapeHtml(def.label)}</span>
      <span class="ed-slot-hint">${escapeHtml(def.hint)}</span>
    </button>`;
}

function groupHtml(g: OptionGroupDef, gid: number): string {
  if (g.kind === 'prompt') {
    return `
      <section class="ed-group" aria-label="${escapeHtml(g.label)}">
        <h2 class="section-title">${escapeHtml(g.label)}</h2>
        <textarea
          class="ed-prompt"
          id="ed-prompt-${gid}"
          rows="2"
          maxlength="2000"
          placeholder="${escapeHtml(g.placeholder ?? 'Add details for the AI (optional)…')}"
          aria-label="${escapeHtml(g.label)}"
        ></textarea>
      </section>`;
  }
  if (g.kind === 'toggle') {
    const on = g.selected === 'on';
    return `
      <section class="ed-group" aria-label="${escapeHtml(g.label)}">
        <button class="ed-toggle${on ? ' is-on' : ''}" data-group="${gid}" data-value="toggle" role="switch" aria-checked="${on}">
          <span class="ed-toggle-label">${escapeHtml(g.label)}</span>
          <span class="ed-toggle-track" aria-hidden="true"><span class="ed-toggle-knob"></span></span>
        </button>
      </section>`;
  }
  if (g.kind === 'presets') {
    return `
      <section class="ed-group" aria-label="${escapeHtml(g.label)}">
        <h2 class="section-title">${escapeHtml(g.label)}${g.required ? ' <span class="ed-req" aria-hidden="true">*</span>' : ''}</h2>
        <div class="ed-presets" role="radiogroup" aria-label="${escapeHtml(g.label)}">
          ${g.options
            .map(
              (o) => `
            <button
              class="ed-preset${o.value === g.selected ? ' selected' : ''}"
              role="radio"
              aria-checked="${o.value === g.selected}"
              data-group="${gid}"
              data-value="${escapeHtml(o.value)}"
            >
              ${
                o.imageUrl
                  ? `<img src="${escapeHtml(o.imageUrl)}" alt="" loading="lazy" />`
                  : `<span class="ed-preset-fallback" aria-hidden="true">${escapeHtml(o.label.slice(0, 1))}</span>`
              }
              <span class="ed-preset-label">${escapeHtml(o.label)}</span>
            </button>`,
            )
            .join('')}
        </div>
      </section>`;
  }
  // chips
  return `
    <section class="ed-group" aria-label="${escapeHtml(g.label)}">
      <h2 class="section-title">${escapeHtml(g.label)}${g.required ? ' <span class="ed-req" aria-hidden="true">*</span>' : ''}</h2>
      <div class="chips" role="radiogroup" aria-label="${escapeHtml(g.label)}">
        ${g.options
          .map(
            (o) => `
          <button
            class="chip${o.value === g.selected ? ' selected' : ''}"
            role="radio"
            aria-checked="${o.value === g.selected}"
            data-group="${gid}"
            data-value="${escapeHtml(o.value)}"
          >${o.swatch ? `<span class="chip-swatch" style="background: ${escapeHtml(o.swatch)};" aria-hidden="true"></span>` : ''}${escapeHtml(o.label)}</button>`,
          )
          .join('')}
      </div>
    </section>`;
}

function tierSectionHtml(tiers: QualityTier[], tier: QualityTier | null): string {
  if (tiers.length === 0) return '';
  return `
    <section class="ed-group" aria-label="Quality">
      <h2 class="section-title">Quality</h2>
      <div class="chips" role="radiogroup" aria-label="Quality tier" id="ed-tiers">
        ${tiers
          .map(
            (q) => `
          <button
            class="chip${q === tier ? ' selected' : ''}"
            role="radio"
            aria-checked="${q === tier}"
            data-tier="${escapeHtml(q)}"
          >${escapeHtml(q.toUpperCase())}</button>`,
          )
          .join('')}
      </div>
      <p class="muted ed-note">Higher quality costs more credits.</p>
    </section>`;
}


const VIDEO_TOOLS: readonly ToolType[] = ['image_to_video', 'face_swap_video', 'transition'];
const PORTRAIT_TOOLS: readonly ToolType[] = [
  'face_swap_image',
  'face_swap_video',
  'hair_salon',
  'makeup_styles',
  'face_expression',
  'retouch',
  'reshape_body',
  'change_outfit',
];

function isVideoOutput(tool: ToolType, t: Template | null): boolean {
  return t ? t.mediaKind === 'video' : VIDEO_TOOLS.includes(tool);
}

/** Short, tool-appropriate photo tips (static guidance, not claims about results). */
function tipsHtml(tool: ToolType): string {
  const tips = PORTRAIT_TOOLS.includes(tool)
    ? ['Use a clear, front-facing portrait', 'Good lighting and high resolution', 'Single person works best']
    : ['Use a sharp, well-lit photo', 'Higher resolution keeps more detail', 'Avoid heavy filters or blur'];
  return `
    <section class="ed-tips" aria-label="Tips for best results">
      <h2 class="ed-tips-h">${icon('lightbulb', { fill: true })}Tips for best results</h2>
      <ul>${tips.map((x) => `<li>${icon('check_circle', { fill: true })}${escapeHtml(x)}</li>`).join('')}</ul>
    </section>`;
}

/**
 * Template preview: "photo + photo → result" for two-photo templates, a
 * Before/After slider when the template provides a `beforeUrl`, else the example.
 */
function previewHtml(t: Template | null): string {
  if (!t) return '';
  if (t.beforeUrl && t.secondBeforeUrl) {
    const [a = 'Your photo', b = 'Second photo'] = inputLabels(t.editorSchema);
    return twoPhotoExampleHtml({
      inputs: [
        { url: t.beforeUrl, label: a },
        { url: t.secondBeforeUrl, label: b },
      ],
      result: t.previewUrl || t.thumbnailUrl,
      className: 'ed-tpx',
    });
  }
  if (t.beforeUrl) {
    return compareSliderHtml({
      before: t.beforeUrl,
      after: t.previewUrl || t.thumbnailUrl,
      ratio: t.mediaKind === 'image' ? '4 / 5' : '1 / 0.82',
      className: 'ed-preview',
      id: 'ed-ba',
      ariaLabel: 'Compare before and after',
    });
  }
  const after = escapeHtml(t.previewUrl || t.thumbnailUrl);
  return `
    <section class="ed-preview${t.mediaKind === 'image' ? ' is-photo' : ''}" aria-label="Example result">
      <img src="${after}" alt="Example: ${escapeHtml(t.title)}" decoding="async" />
      <span class="ed-ba-label is-example">Example</span>
    </section>`;
}

function ctaInner(label: string, pill: string): string {
  return `<span class="gn-l">${icon('auto_awesome', { fill: true })}${label}</span><span class="gn-c">${pill}</span>`;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

let renderSeq = 0;
/** Cleanup for the previous editor mount (balance subscription). */
let prevCleanup: (() => void) | null = null;

export function renderEditor(root: HTMLElement, rawToolParam: string, query: URLSearchParams): void {
  // Links use hyphens (#/editor/image-to-video); tool ids use underscores.
  const toolParam = rawToolParam.replace(/-/g, '_');
  if (toolParam === 'image_to_video') {
    // Image to Video has its own screen (template / customize modes).
    renderImageToVideo(root, query);
    return;
  }
  if (toolParam === 'change_outfit') {
    // Change Outfit has its own screen (style rail + pose/background switches).
    renderChangeOutfit(root, query);
    return;
  }
  if (toolParam === 'hair_salon') {
    // Hair Salon has its own screen (hairstyle rail + optional hair color).
    renderHairSalon(root, query);
    return;
  }
  if (toolParam === 'makeup_styles') {
    // Makeup Styles has its own screen (style rail + intensity + skin texture).
    renderMakeupStyles(root, query);
    return;
  }
  if (toolParam === 'face_expression') {
    // Face Expression has its own screen (expression rail + intensity + consent notice).
    renderFaceExpression(root, query);
    return;
  }
  if (toolParam === 'photo_enhance') {
    // Photo Enhance has its own screen (enhance types, no templates).
    renderPhotoEnhance(root, query);
    return;
  }
  if (toolParam === 'remove_object') {
    // Remove Object has its own screen (zoomable brush canvas / Remove Text, no templates).
    renderRemoveObject(root, query);
    return;
  }
  if (toolParam === 'retouch') {
    // Retouch has its own screen (multi-select touch-ups + intensity, no templates).
    renderRetouch(root, query);
    return;
  }
  if (toolParam === 'reshape_body') {
    // Hidden for now (owner, 2026-10-24): results barely change the body. Old
    // links land on Create. Set RESHAPE_BODY_ENABLED to bring the screen back.
    if (!RESHAPE_BODY_ENABLED) {
      toast('Reshape Body is not available right now.');
      navigate('/create');
      return;
    }
    renderReshapeBody(root, query);
    return;
  }
  if (toolParam === 'combine') {
    // Combine: 2–5 photos joined with a transition, rendered on our server (no AI).
    renderCombine(root, query);
    return;
  }
  if (toolParam === 'transition') {
    // Transition (AI): a start frame and an end frame, the AI makes the motion between them.
    renderTransition(root, query);
    return;
  }
  if (toolParam === 'ingredients') {
    // Ingredients to Video has its own screen (2–4 numbered images + scene text, no templates).
    renderIngredients(root, query);
    return;
  }
  const seq = ++renderSeq;
  const alive = (): boolean => seq === renderSeq && root.isConnected;

  const slug = query.get('template')?.trim() || null;
  const wantedTier = query.get('quality');
  const wantedDuration = Number(query.get('duration'));

  const load = async (): Promise<void> => {
    // Drop the previous mount's balance subscription (also covers retries).
    if (prevCleanup) {
      prevCleanup();
      prevCleanup = null;
    }
    if (alive()) root.innerHTML = skeletonHtml();

    // Fetch everything we need in parallel. Balance failure is non-fatal
    // (re-checked on Generate); pricing failure IS fatal (costs are data).
    const [tplResult, pricingResult, balanceResult] = await Promise.allSettled([
      slug ? api.getTemplate(slug) : Promise.resolve(null),
      api.getPricing(),
      refreshCredits().catch(() => null),
    ]);
    if (!alive()) return;
    void balanceResult;

    if (tplResult.status === 'rejected') {
      const err = tplResult.reason;
      if (err instanceof ApiError && err.status === 404) {
        root.innerHTML = errorHtml('This template no longer exists.', false);
      } else {
        root.innerHTML = errorHtml("Couldn't load the editor. Check your connection.", true);
        toastApiError(err, "Couldn't load the editor.");
      }
      bindStaticActions();
      return;
    }
    if (pricingResult.status === 'rejected') {
      root.innerHTML = errorHtml("Couldn't load pricing. Check your connection.", true);
      toastApiError(pricingResult.reason, "Couldn't load pricing.");
      bindStaticActions();
      return;
    }

    const template: Template | null = tplResult.status === 'fulfilled' ? tplResult.value : null;
    const pricing = pricingResult.value;

    const routeTool = (VALID_TOOLS as readonly string[]).includes(toolParam)
      ? (toolParam as ToolType)
      : null;
    // A template always wins: its tool is authoritative for the job submit.
    const tool: ToolType | null = template?.tool ?? routeTool;
    if (!tool) {
      root.innerHTML = errorHtml(`Unknown tool "${toolParam}".`, false);
      return;
    }

    const spec: EditorSpec | null = template
      ? normalizeTemplateSchema(template)
      : defaultSpec(tool, pricing);
    if (!spec) {
      root.innerHTML = errorHtml(
        `The ${TOOL_LABELS[tool]} editor isn't available yet. Try a template instead.`,
        false,
      );
      return;
    }

    mount(tool, spec, pricing, preferredTier(spec.tiers, wantedTier), template, wantedDuration);
  };

  const bindStaticActions = (): void => {
    root.querySelectorAll<HTMLElement>('[data-action]').forEach((el) => {
      if (el.dataset.action === 'retry') {
        el.addEventListener('click', () => void load());
      }
    });
  };

  const mount = (
    tool: ToolType,
    spec: EditorSpec,
    pricing: CreditPricingConfig,
    initialTier: QualityTier | null,
    template: Template | null,
    wantedDuration = 0,
  ): void => {
    let tier = initialTier;
    const durations = durationsFor(pricing, tool);
    let duration: VideoDurationSec | null = durations.includes(wantedDuration as VideoDurationSec)
      ? (wantedDuration as VideoDurationSec)
      : durations.includes(5)
        ? 5
        : (durations[0] ?? null);
    let generating = false;
    const slots: SlotState[] = spec.uploads.map((def) => ({
      def,
      status: 'empty',
      progress: 0,
      fileId: null,
      previewUrl: null,
      file: null,
      error: null,
    }));

    const cost = (): number => computeCost(spec, pricing, tool, tier, duration);

    /** First missing required input, for the disabled-state hint. */
    const missingInput = (): string | null => {
      for (const s of slots) {
        if (s.def.required && (s.status !== 'ready' || !s.fileId)) {
          return s.status === 'uploading' ? 'Uploading…' : `Add ${s.def.label.toLowerCase()}`;
        }
      }
      for (const g of spec.groups) {
        if (g.required && !g.selected) return `Choose ${g.label.toLowerCase()}`;
      }
      return null;
    };

    const paintSlots = (): void => {
      slots.forEach((slot, i) => {
        const host = root.querySelector<HTMLElement>(`#ed-slot-${i}`);
        if (host) host.innerHTML = slotHtml(slot);
      });
    };

    const paintCta = (): void => {
      const btn = root.querySelector<HTMLButtonElement>('#ed-generate');
      const warn = root.querySelector<HTMLElement>('#ed-warn');
      if (!btn || !warn) return;
      const c = cost();
      const missing = missingInput();
      const balance = getBalance();

      if (generating) {
        btn.disabled = true;
        btn.innerHTML = ctaInner('Starting…', `\u2726 ${c} Credits`);
        warn.hidden = true;
        return;
      }
      if (missing) {
        // Stays tappable: a tap signs the visitor in or opens the photo picker.
        btn.disabled = false;
        btn.dataset.mode = 'generate';
        btn.innerHTML = ctaInner('Generate Now', `\u2726 ${c} Credits`);
        warn.hidden = false;
        warn.className = 'ed-warn is-info';
        warn.textContent = missing;
        return;
      }
      if (balance !== null && balance < c) {
        btn.disabled = false;
        btn.dataset.mode = 'paywall';
        btn.innerHTML = ctaInner('Get credits', `Need ${c} \u2726`);
        warn.hidden = false;
        warn.className = 'ed-warn is-warn';
        warn.textContent = `Not enough credits for this generation.`;
        return;
      }
      btn.disabled = false;
      btn.dataset.mode = 'generate';
      btn.innerHTML = ctaInner('Generate Now', `\u2726 ${c} Credits`);
      warn.hidden = true;
    };

    const paintTiers = (): void => {
      const host = root.querySelector<HTMLElement>('#ed-tiers');
      if (!host) return;
      host.querySelectorAll<HTMLButtonElement>('[data-tier]').forEach((b) => {
        const selected = b.dataset.tier === tier;
        b.classList.toggle('selected', selected);
        b.setAttribute('aria-checked', String(selected));
      });
    };

    const paintDurations = (): void => {
      const host = root.querySelector<HTMLElement>('#ed-durations');
      if (!host) return;
      host.querySelectorAll<HTMLButtonElement>('[data-duration]').forEach((b) => {
        const d = Number(b.dataset.duration) as VideoDurationSec;
        const selected = d === duration;
        b.classList.toggle('selected', selected);
        b.setAttribute('aria-checked', String(selected));
        const price = b.querySelector<HTMLElement>('[data-dprice]');
        if (price) price.textContent = `\u2726 ${computeCost(spec, pricing, tool, tier, d)}`;
      });
    };

    const paintGroup = (gid: number): void => {
      const g = spec.groups[gid];
      if (!g) return;
      if (g.kind === 'toggle') {
        const btn = root.querySelector<HTMLButtonElement>(`.ed-toggle[data-group="${gid}"]`);
        if (btn) {
          const on = g.selected === 'on';
          btn.classList.toggle('is-on', on);
          btn.setAttribute('aria-checked', String(on));
        }
        return;
      }
      if (g.kind === 'prompt') return; // textarea keeps its own state
      root
        .querySelectorAll<HTMLButtonElement>(`[data-group="${gid}"][data-value]`)
        .forEach((b) => {
          const selected = b.dataset.value === g.selected;
          b.classList.toggle('selected', selected);
          b.setAttribute('aria-checked', String(selected));
        });
    };

    // -- Uploads ----------------------------------------------------------

    const startUpload = async (slot: SlotState, file: File): Promise<void> => {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        toast('Please choose a JPEG, PNG or WebP image.');
        return;
      }
      if (file.size > CLIENT_MAX_BYTES) {
        toast('That image is too large. Please pick one under 25 MB.');
        return;
      }
      // Revoke the previous preview object URL, if any.
      if (slot.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(slot.previewUrl);

      slot.file = file;
      slot.status = 'uploading';
      slot.progress = 0;
      slot.error = null;
      slot.previewUrl = URL.createObjectURL(file); // instant preview
      paintSlots();
      paintCta();
      try {
        const req = await api.requestUpload({
          purpose: 'generation_input',
          contentType: file.type,
          contentLength: file.size,
        });
        await api.putUploadBytes(req.uploadUrl, file, req.uploadHeaders, (r) => {
          if (!alive()) return;
          slot.progress = r;
          const bar = root.querySelector<HTMLElement>(
            `#ed-slot-${slots.indexOf(slot)} .ed-progress span`,
          );
          const label = root.querySelector<HTMLElement>(
            `#ed-slot-${slots.indexOf(slot)} .ed-slot-label`,
          );
          if (bar) bar.style.width = `${Math.round(r * 100)}%`;
          if (label) label.textContent = `${Math.round(r * 100)}%`;
        });
        const meta: UploadFileMeta = await api.completeUpload(req.fileId);
        if (!alive()) return;
        if (meta.status !== 'ready') throw new Error(meta.error || 'Image processing failed.');
        slot.status = 'ready';
        slot.fileId = meta.id;
        // Prefer the server thumbnail; fall back to the local preview.
        slot.previewUrl = meta.thumbnails?.thumb256 ?? meta.url ?? slot.previewUrl;
      } catch (err) {
        if (!alive()) return;
        slot.status = 'error';
        slot.fileId = null;
        slot.error =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Upload failed. Tap to retry.';
        toastApiError(err, 'Upload failed. Tap the box to retry.');
      }
      if (alive()) {
        paintSlots();
        paintCta();
      }
    };

    const clearSlot = (slot: SlotState): void => {
      const oldId = slot.fileId;
      if (slot.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(slot.previewUrl);
      slot.status = 'empty';
      slot.progress = 0;
      slot.fileId = null;
      slot.previewUrl = null;
      slot.file = null;
      slot.error = null;
      paintSlots();
      paintCta();
      // Best-effort: don't leave orphaned files behind.
      if (oldId) void api.deleteUpload(oldId).catch(() => undefined);
    };

    // -- Generate ---------------------------------------------------------

    const buildPrompt = (): string => {
      const parts: string[] = [];
      spec.groups.forEach((g, gid) => {
        if (g.kind !== 'prompt') return;
        const el = root.querySelector<HTMLTextAreaElement>(`#ed-prompt-${gid}`);
        const text = el?.value.trim();
        if (text) parts.push(text);
      });
      for (const g of spec.groups) {
        if (g.kind === 'prompt' || g.key === 'quality' || !g.selected) continue;
        const opt = g.options.find((o) => o.value === g.selected);
        if (opt) parts.push(`${g.label}: ${opt.label}`);
      }
      return parts.join('; ');
    };

    const onGenerate = async (): Promise<void> => {
      if (generating) return;
      if (!requireAccount('Sign in to generate.')) return;
      const missing = missingInput();
      if (missing) {
        toast(missing);
        // No photo yet: open the first empty slot's picker right away.
        root.querySelector<HTMLElement>('[data-slot-action="pick"]')?.click();
        return;
      }
      const c = cost();
      const mode = root.querySelector<HTMLButtonElement>('#ed-generate')?.dataset.mode;
      if (mode === 'paywall') {
        navigate('/paywall');
        return;
      }
      let balance = getBalance();
      if (balance === null) balance = await refreshCredits().catch(() => null);
      if (!alive()) return;
      if (balance !== null && balance < c) {
        navigate('/paywall');
        return;
      }
      generating = true;
      paintCta();
      try {
        const inputFileIds = slots
          .filter((s) => s.def.required || s.fileId)
          .map((s) => s.fileId)
          .filter((id): id is string => id !== null);
        const prompt = buildPrompt();
        const { job } = await api.submitJob({
          tool,
          templateId: spec.templateId,
          inputFileIds,
          qualityTier: tier ?? undefined,
          durationSec: duration ?? undefined,
          prompt: prompt || undefined,
          idempotencyKey: newId(),
        });
        void refreshCredits().catch(() => undefined);
        navigate(`/result/${job.id}`);
      } catch (err) {
        if (!alive()) return;
        if (
          err instanceof ApiError &&
          (err.status === 402 || /insufficient|credit/i.test(err.code))
        ) {
          toast('Not enough credits.');
          navigate('/paywall');
        } else {
          toastApiError(err, 'Could not start generation. Please try again.');
        }
      } finally {
        if (alive()) {
          generating = false;
          paintCta();
        }
      }
    };

    // -- Static render ----------------------------------------------------

    const goBack = (): void => {
      if (window.history.length > 1) window.history.back();
      else navigate('/home');
    };

    const presetGroups = spec.groups
      .map((g, gid) => ({ g, gid }))
      .filter((x) => x.g.kind === 'presets' && x.g.options.length > 1);
    // A preset pinned by the template has a single option: nothing to choose,
    // so it is not shown (its value is still sent with the job).
    const otherGroups = spec.groups
      .map((g, gid) => ({ g, gid }))
      .filter((x) => x.g.kind !== 'presets');
    const subtitle = isVideoOutput(tool, template)
      ? 'Create your own amazing video'
      : 'Create your own amazing photo';
    const photosTitle = slots.length > 1 ? 'Input Photos' : 'Input Photo';

    root.innerHTML = `
      <div class="ed">
        <header class="ed-head">
          <button class="ed-back" data-action="back" aria-label="Back">${icon('arrow_back')}</button>
          <div class="ed-headtx">
            <h1 class="ed-title">${escapeHtml(spec.title)}</h1>
            <p class="ed-sub">${subtitle}</p>
          </div>
          <span class="ed-head-spacer" aria-hidden="true"></span>
        </header>

        <div class="stack ed-body">
          ${previewHtml(template)}
          ${
            spec.description && !template
              ? `<p class="muted ed-desc">${escapeHtml(spec.description)}</p>`
              : ''
          }

          ${presetGroups.map(({ g, gid }) => groupHtml(g, gid)).join('')}

          <section aria-label="Photos" class="ed-group">
            <h2 class="section-title">${photosTitle}</h2>
            <div class="ed-uploads cols-${slots.length > 1 ? '2' : '1'}">
              ${slots.map((_, i) => `<div class="ed-slot-host" id="ed-slot-${i}"></div>`).join('')}
            </div>
          </section>

          ${tierSectionHtml(spec.tiers, tier)}
          ${durationSectionHtml(spec, pricing, tool, tier, durations, duration)}
          ${otherGroups.map(({ g, gid }) => groupHtml(g, gid)).join('')}
          ${tipsHtml(tool)}
        </div>

        <div class="ed-cta">
          <p class="ed-warn" id="ed-warn" hidden></p>
          <button class="btn btn-generate" id="ed-generate" data-mode="generate"></button>
          <p class="ed-foot">Failed runs are refunded automatically.</p>
        </div>

        <input type="file" id="ed-file" accept="image/*" hidden aria-hidden="true" />
      </div>`;

    // Before/After slider (shared component)
    bindCompareSliders(root);

    paintSlots();
    paintCta();

    // Balance can change while the editor is open (e.g. after a purchase).
    const unsub = creditsChanged.on(() => {
      if (alive()) paintCta();
    });
    prevCleanup = () => unsub();

    // -- Events (delegated) -------------------------------------------------

    const fileInput = root.querySelector<HTMLInputElement>('#ed-file')!;
    let pendingSlot = -1;

    fileInput.addEventListener('change', () => {
      if (!alive()) return;
      const file = fileInput.files?.[0];
      fileInput.value = '';
      if (!file || pendingSlot < 0) return;
      const slot = slots[pendingSlot];
      pendingSlot = -1;
      if (slot) void startUpload(slot, file);
    });

    root.querySelector('[data-action="back"]')?.addEventListener('click', goBack);

    root.addEventListener('click', (e) => {
      if (!alive()) return;
      const tierBtn = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-tier]');
      if (tierBtn?.dataset.tier) {
        tier = tierBtn.dataset.tier as QualityTier;
        paintTiers();
        paintDurations();
        paintCta();
        return;
      }
      const durBtn = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-duration]');
      if (durBtn?.dataset.duration) {
        duration = Number(durBtn.dataset.duration) as VideoDurationSec;
        paintDurations();
        paintCta();
        return;
      }
      const optBtn = (e.target as HTMLElement).closest<HTMLButtonElement>(
        '[data-group][data-value]',
      );
      if (optBtn && optBtn.dataset.group !== undefined && optBtn.dataset.value !== undefined) {
        const gid = Number(optBtn.dataset.group);
        const g = spec.groups[gid];
        if (!g) return;
        if (g.kind === 'toggle') {
          g.selected = g.selected === 'on' ? 'off' : 'on';
        } else {
          g.selected = optBtn.dataset.value;
        }
        paintGroup(gid);
        paintCta();
        return;
      }
      const slotBtn = (e.target as HTMLElement).closest<HTMLElement>('[data-slot-action]');
      if (slotBtn) {
        const host = slotBtn.closest<HTMLElement>('.ed-slot-host');
        const idx = host ? Number(host.id.replace('ed-slot-', '')) : -1;
        const slot = slots[idx];
        if (!slot) return;
        const action = slotBtn.dataset.slotAction;
        if (action === 'pick' || action === 'replace' || action === 'retry') {
          if (slot.status === 'uploading') return;
          if (action === 'retry' && slot.file) {
            void startUpload(slot, slot.file);
          } else {
            // Guests can explore the editor; an account is needed to upload.
            if (!requireSignIn('Sign in to upload your photo.')) return;
            pendingSlot = idx;
            withUploadGuide(tool, () => fileInput.click());
          }
        } else if (action === 'remove') {
          clearSlot(slot);
        }
      }
    });

    const genBtn = root.querySelector<HTMLButtonElement>('#ed-generate');
    genBtn?.addEventListener('click', () => void onGenerate());
  };

  void load();
}
